import React from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import usePaging from 'hooks/usePaging';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron-thin-left.svg';

import './Pagination.scss';

interface Props {
  pagingInfo: ReturnType<typeof usePaging>;
}

const Pagination: React.FC<Props> = ({ pagingInfo }) => {
  const { t } = useTranslation();
  const {
    nextPage,
    prevPage,
    pageSize,
    page,
    offset,
    pageCount,
    hasPrev,
    hasNext,
    setPage,
    itemCount,
  } = pagingInfo;

  let pages = [-2, -1, 0, 1, 2];

  pages = page <= 2 ? [...pages, 3] : pages;
  pages = page <= 1 ? [...pages, 4] : pages;
  pages = page >= pageCount ? [-4, ...pages] : pages;
  pages = page >= pageCount - 1 ? [-3, ...pages] : pages;

  const visiblePageNumbers = pages
    .filter(item => {
      const pageNumber = page + item;
      const isVisible = pageNumber > 0 && pageNumber <= pageCount;
      return isVisible;
    })
    .map(item => page + item);

  const firstVisiblePage = visiblePageNumbers[0];
  const lastVisiblePage = visiblePageNumbers[visiblePageNumbers.length - 1];

  const showLastPageSepertly = pageCount - lastVisiblePage >= 1;

  return (
    <div className="pagination no-select" key={page}>
      <div className="pages">
        <div className={classNames('page', { pageDisabled: !hasPrev })} onClick={prevPage}>
          <ChevronIcon />
        </div>

        {firstVisiblePage >= 3 && (
          <div onClick={() => setPage(1)} className="page">
            {1}
          </div>
        )}

        {firstVisiblePage >= 3 && (
          <div
            className="page"
            onClick={() => {
              setPage(Math.ceil(firstVisiblePage / 2));
            }}
          >
            ...
          </div>
        )}

        {visiblePageNumbers.map(pageNumber => {
          return (
            <div
              key={pageNumber}
              className={classNames('page', { pageSelected: pageNumber === page })}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </div>
          );
        })}

        {pageCount - lastVisiblePage >= 2 && (
          <div
            className="page"
            onClick={() => {
              setPage(lastVisiblePage + Math.ceil((pageCount - lastVisiblePage) / 2));
            }}
          >
            ...
          </div>
        )}
        {showLastPageSepertly && (
          <div onClick={() => setPage(pageCount)} className="page ">
            {pageCount}
          </div>
        )}

        <div
          className={classNames('page', 'rotate', { pageDisabled: !hasNext })}
          onClick={nextPage}
        >
          <ChevronIcon />
        </div>
      </div>
      <div className="info">
        {t('common:PaginationInfoText', {
          start: itemCount > 0 ? offset + 1 : 0,
          end: Math.min(offset + pageSize, itemCount),
          total: itemCount,
        })}
      </div>
    </div>
  );
};

export default Pagination;
